import { useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PushPinIcon from '@mui/icons-material/PushPin';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { api, type Announcement, type DeckSummary } from '../../lib/api';
import { navigate, paths } from '../../lib/router';
import { useDepartmentName } from '../../store/departmentStore';
import { SlideThumbnail } from '../SlideThumbnail';
import { deckGrid, timeAgo } from './portalStyles';

/** Page title row with optional description and actions. */
export function PageHeader({ title, description, actions, icon }: { title: string; description?: string; actions?: ReactNode; icon?: ReactNode }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 4 }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        {icon}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      {actions && <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>{actions}</Stack>}
    </Stack>
  );
}

export function Section({ title, icon, action, children }: { title: string; icon?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <Box component="section" sx={{ mb: 5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2, minHeight: 36 }}>
        {icon && <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>}
        <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'chrome.border',
        borderRadius: 2.5,
        py: 4,
        px: 2,
        textAlign: 'center',
        color: 'text.secondary',
        typography: 'body2',
      }}
    >
      {children}
    </Box>
  );
}

export function DeckCard({ deck, onOpen, badge, menu }: { deck: DeckSummary; onOpen: () => void; badge?: ReactNode; menu?: ReactNode }) {
  const departmentName = useDepartmentName();
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2.5,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'box-shadow 150ms, border-color 150ms',
        '&:hover': { borderColor: 'chrome.border', boxShadow: '0 6px 20px -8px rgba(16,24,40,0.2)' },
      }}
    >
      <ButtonBase onClick={onOpen} sx={{ display: 'block', width: '100%', textAlign: 'left' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <SlideThumbnail slide={deck.first_slide} />
        </Box>
        <Box sx={{ p: 1.5, pr: menu ? 5 : 1.5 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {deck.title}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mt: 0.5, minWidth: 0 }}>
            <Chip size="small" label={departmentName(deck.category)} sx={{ height: 20, fontSize: 11 }} />
            {badge}
            <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
              {deck.owner_name} · {timeAgo(deck.updated_at)}
            </Typography>
          </Stack>
        </Box>
      </ButtonBase>
      {menu && <Box sx={{ position: 'absolute', right: 6, bottom: 10 }}>{menu}</Box>}
    </Box>
  );
}

export function DeckGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Box sx={deckGrid}>
      {Array.from({ length: count }, (_, i) => (
        <Box key={i} sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
          <Skeleton variant="rectangular" sx={{ width: '100%', height: 'auto', aspectRatio: '16 / 9' }} />
          <Box sx={{ p: 1.5 }}>
            <Skeleton width="70%" />
            <Skeleton width="45%" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AnnouncementCard({
  item,
  isAdmin,
  onEdit,
  onDelete,
  onAcknowledge,
  onShowReads,
}: {
  item: Announcement;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** Staff confirm "I have read this". */
  onAcknowledge: () => Promise<void>;
  /** Admins open the read-confirmation report. */
  onShowReads: () => void;
}) {
  const [acking, setAcking] = useState(false);
  const departmentName = useDepartmentName();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <Box
      component="article"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        borderRadius: 2.5,
        border: 1,
        borderColor: item.pinned ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={departmentName(item.category)}
          onClick={() => navigate(paths.dept(item.category))}
          sx={{ height: 22 }}
        />
        {item.status === 'scheduled' && item.publish_at && (
          <Chip size="small" icon={<ScheduleIcon />} label={`Scheduled ${formatWhen(item.publish_at)}`} sx={{ height: 22 }} />
        )}
        {item.status === 'expired' && <Chip size="small" label="Expired" sx={{ height: 22 }} />}
        {item.pinned && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'primary.main' }}>
            <PushPinIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
              Pinned
            </Typography>
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        {isAdmin && (
          <>
            <IconButton size="small" aria-label={`Actions for ${item.title}`} onClick={(e) => setAnchor(e.currentTarget)}>
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchor}
              open={Boolean(anchor)}
              onClose={() => setAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onEdit();
                }}
              >
                <ListItemIcon>
                  <EditOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Edit" />
              </MenuItem>
              <MenuItem
                sx={{ color: 'error.main' }}
                onClick={() => {
                  setAnchor(null);
                  onDelete();
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Delete" />
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>
      <Typography variant="subtitle2" component="h3">
        {item.title}
      </Typography>
      {item.body && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'pre-line', display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {item.body}
        </Typography>
      )}
      {item.deck_id && item.deck_title && (
        <ButtonBase
          onClick={() => navigate(paths.view(item.deck_id!))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1,
            mt: 0.5,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            textAlign: 'left',
            '&:hover': { bgcolor: 'chrome.subtle' },
          }}
        >
          <Box sx={{ width: 96, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
            <SlideThumbnail slide={item.deck_first_slide} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {item.deck_title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
              View slides
            </Typography>
          </Box>
        </ButtonBase>
      )}
      {item.attachment_id && item.attachment_file_name && (
        <Button
          component="a"
          href={api.documents.fileUrl(item.attachment_id)}
          variant="outlined"
          color="neutral"
          size="small"
          startIcon={<AttachFileIcon />}
          sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.attachment_title || item.attachment_file_name}
            {item.attachment_size ? ` · ${formatBytes(item.attachment_size)}` : ''}
          </Box>
        </Button>
      )}
      {item.require_ack && item.status === 'live' && (
        <Box sx={{ mt: 0.5 }}>
          {item.my_read_at ? (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', color: 'success.main' }}>
              <CheckCircleIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 600 }}>
                You confirmed reading this on {formatWhen(item.my_read_at)}
              </Typography>
            </Stack>
          ) : (
            <Button
              size="small"
              variant="contained"
              disabled={acking}
              startIcon={acking ? <CircularProgress size={14} color="inherit" /> : <TaskAltIcon />}
              onClick={() => {
                setAcking(true);
                void onAcknowledge().finally(() => setAcking(false));
              }}
            >
              I have read this
            </Button>
          )}
        </Box>
      )}
      <Stack direction="row" sx={{ mt: 'auto', pt: 0.5, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ flex: 1 }}>
          {item.author_name} · {timeAgo(item.publish_at ?? item.created_at)}
          {item.expires_at && item.status !== 'expired' ? ` · until ${formatWhen(item.expires_at)}` : ''}
        </Typography>
        {isAdmin && item.require_ack && (
          <Button size="small" color="neutral" onClick={onShowReads} sx={{ height: 24, px: 1 }}>
            Read by {item.read_count}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
